import { Bug, Filter, ShieldCheck, Sparkles, Wrench } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppName } from '@/hooks/useAppName';
import { cn } from '@/lib/utils';
import { CHANGELOG, ChangelogCategory, LATEST_VERSION } from '@/data/changelog';

const SEEN_VERSION_KEY = 'etabli_changelog_seen_version';

const CATEGORY_CONFIG: Record<ChangelogCategory, {
  icon: React.ElementType;
  labelKey: string;
  className: string;
}> = {
  feature: {
    icon: Sparkles,
    labelKey: 'changelog.category.feature',
    className: 'bg-primary/10 text-primary border-primary/20',
  },
  improvement: {
    icon: Wrench,
    labelKey: 'changelog.category.improvement',
    className: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
  },
  fix: {
    icon: Bug,
    labelKey: 'changelog.category.fix',
    className: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
  },
  security: {
    icon: ShieldCheck,
    labelKey: 'changelog.category.security',
    className: 'bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20',
  },
};

export const ChangelogPage: React.FC = () => {
  const { t } = useTranslation();
  const appName = useAppName();
  const [filter, setFilter] = useState<ChangelogCategory | 'all'>('all');

  useEffect(() => {
    localStorage.setItem(SEEN_VERSION_KEY, LATEST_VERSION);
  }, []);

  return (
    <div className="max-w-3xl mx-auto py-8 px-4 animate-fade-in">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Sparkles className="h-6 w-6 text-primary" />
          </div>
          <h1 className="text-3xl font-bold text-foreground">
            {t('changelog.title', "What's New")}
          </h1>
        </div>
        <p className="text-muted-foreground ml-14">
          {t('changelog.subtitle', 'Latest updates and improvements to {{app}}.', { app: appName })}
        </p>
      </div>

      {/* Category filter */}
      <div className="flex items-center gap-2 mb-8 flex-wrap">
        <Filter className="h-4 w-4 text-muted-foreground" />
        {(['all', 'feature', 'improvement', 'fix', 'security'] as const).map((cat) => (
          <button
            key={cat}
            onClick={() => setFilter(cat)}
            className={cn(
              'px-3 py-1 rounded-full text-sm font-medium border transition-all duration-150',
              filter === cat
                ? cat === 'all'
                  ? 'bg-foreground text-background border-foreground'
                  : cn(CATEGORY_CONFIG[cat as ChangelogCategory].className, 'border')
                : 'bg-muted/30 text-muted-foreground border-border hover:bg-muted',
            )}
          >
            {cat === 'all' ? t('changelog.filter.all', 'All') : t(CATEGORY_CONFIG[cat].labelKey)}
          </button>
        ))}
      </div>

      {/* Changelog entries */}
      <div className="relative">
        {/* Timeline line */}
        <div className="absolute left-[7px] top-2 bottom-0 w-px bg-border" />

        <div className="space-y-10">
          {CHANGELOG.map((entry, entryIdx) => {
            const filteredItems = entry.items.filter(
              (item) => filter === 'all' || item.category === filter,
            );
            if (!filteredItems.length) return null;

            const isLatest = entryIdx === 0;

            return (
              <div key={entry.version} className="relative pl-8 animate-slide-up">
                {/* Timeline dot */}
                <div
                  className={cn(
                    'absolute left-0 top-1.5 h-4 w-4 rounded-full border-2 transition-colors',
                    isLatest
                      ? 'bg-primary border-primary'
                      : 'bg-card border-border',
                  )}
                />

                {/* Version header */}
                <div className="flex items-center gap-3 mb-4">
                  <h2 className="text-xl font-bold text-foreground">
                    v{entry.version}
                  </h2>
                  {isLatest && (
                    <span className="px-2 py-0.5 text-[11px] font-semibold bg-primary/10 text-primary rounded-full border border-primary/20">
                      {t('changelog.latest', 'Latest')}
                    </span>
                  )}
                  <span className="text-sm text-muted-foreground ml-auto">
                    {new Date(entry.date).toLocaleDateString(undefined, {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </span>
                </div>

                {/* Items */}
                <div className="space-y-3">
                  {filteredItems.map((item, itemIdx) => {
                    const config = CATEGORY_CONFIG[item.category];
                    const Icon = config.icon;
                    return (
                      <div
                        key={itemIdx}
                        className="glass-card rounded-lg p-4 hover-card"
                      >
                        <div className="flex items-start gap-3">
                          <span
                            className={cn(
                              'inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold border flex-shrink-0 mt-0.5',
                              config.className,
                            )}
                          >
                            <Icon className="h-3 w-3" />
                            {t(config.labelKey)}
                          </span>
                          <div>
                            <p className="text-sm font-semibold text-foreground">
                              {t(item.titleKey, item.titleKey)}
                            </p>
                            <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                              {t(item.descriptionKey, item.descriptionKey)}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ChangelogPage;
